import Vue from 'vue';
import axios from 'axios';
import store from '@/store';
import Router from '../router';
import {
  BusinessProfile,
  BusinessProfileOrg,
  BusinessProfileOrgResponse,
  BusinessProfileResponse,
  BusinessProfileUsers,
  BusinessProfileUsersResponse,
  CapabilitiesList,
  CapabilitiesListResponse,
  CaseStudy,
  CertificatesEntity,
  CertificateTypes,
  CommsEntity,
  Communications,
  CountriesEntity,
  Edos,
  EdosEntity,
  GeneralFeedback,
  ICorrespondenceGetParams,
  ICountry,
  IInterest,
  IMarketRequest,
  IProfileComplete,
  ISurveyResponse,
  ITimerUpdate,
  IVerify,
  LocationsAttributesEntity,
  MRContactUser,
  MRFBPayload,
  Profile,
  ProfileResponse,
  Project,
  ProjectSupplyChain,
  ProjectSupplyChainResponse,
  SingleSupplyChain,
  SingleSupplyChainResponse,
  SuppliersList,
  SuppliersListResponse,
  SupplyChains,
  SupplyChainsResponse,
  UpdateProjectSearchTags,
  User,
  UserFeedbackResponse,
  UserResponse,
  UsersEntity,
  UserSubmit,
  IChangePassword,
  Organization,
  PublicSupplierSearchList,
  IProductService,
  BusinessInsuranceAndCertificates,
  BusinessCategory,
  BillingDetails,
} from '@/store/models';
import users from '@/store/modules/users';
import { Helpers } from '@/tools/helpers';

const Qs = require('qs');

export const economxapi = axios.create({
  baseURL: process.env.VUE_APP_API,
  headers: { 'Content-Type': 'application/json' }
});

// Format nested params correctly
economxapi.interceptors.request.use((config) => {
  config.paramsSerializer = (params) => {
    // Qs is already included in the Axios package
    return Qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: false,
    });
  };

  return config;
});

economxapi.interceptors.response.use(
  (success) => success,
  (error) => {
    if (error && error.response && error.response.status) {
      switch (error.response.status) {
        case 401:
          //  Token has expired
          // console.log('error 401', (error && error.response && error.response.data) ? error.response.data : error);
          return Vue.swal('Logged Out', 'Your session has expired. Please login again.', 'info').then((result) => {
            clearToken().then((res) => {
              Router.push('/');
            });
          });
        case 403:
          //  Permission Denied
          // console.log('error 403', error.response);
          return Vue.swal('Authorisation Error', 'You do not have permissions to perform this action', 'error').then(
            (result) => {
              Router.push('/');
            },
          );
        case 422:
          //  Unprocessable entity
          // console.log('error 422', error.response);
          return Vue.swal(
            'Error',
            error.response.data && error.response.data.description
              ? `${error.response.data.description}`
              : 'Something went wrong, please try again later or contact our support team',
            'error',
          ).then((result) => {
            if (error.response.data && error.response.data.description && error.response.data.description.indexOf('Cannot de-activate') >= 0)
              return true;
            else
              console.log('result', result);

          });
        case 500:
          // Any other server error
          // console.log('error 500', error.response);
          return Vue.swal(
            'Error',
            error.response.data && error.response.data.description
              ? `${error.response.data.description}`
              : 'Something went wrong, please try again later or contact our support team',
            'error',
          ).then((result) => {
            console.log('result', result);
          });
        case 409:
          return
        default:
          return Vue.swal(
            'Error',
            error.response.data && error.response.data.description
              ? `${error.response.data.description}`
              : 'Something went wrong, please try again later or contact our support team',
            'error',
          ).then((result) => {
            console.log('result', result);
          });
      }
    } else {
      return error;
    }
  },
);

export function setUser(user: User) {
  // Check if the localStorage current_user is null then set it to the
  // given user object and set the request Authorization header to user's auth_token
  localStorage.setItem('current_user', JSON.stringify(user));
  economxapi.defaults.headers.common.Authorization = user.auth_token;
}

export async function clearToken() {
  // Clear localStorage and delete Authorization header
  delete economxapi.defaults.headers.common.Authorization;
  store.commit('setUser', null);
  localStorage.clear();
  sessionStorage.clear();
  return null;
}

// Converts object to FormData
function getFormData(object: any) {
  const formData = new FormData();
  Object.keys(object).forEach((key) => {
    formData.append(key, object[key]);
  });
  return formData;
}

export async function loginUser(user: UserSubmit): Promise<User> {
  const response = await economxapi.post(
    '/logins' + '?' + 'password=' + encodeURIComponent(user.password) + '&username=' + encodeURIComponent(user.email),
  );
  return (response.data as UserResponse).user;
}

export async function dashboardStats(project_id?: number): Promise<any> {
  const response = await economxapi.get(
    '/stats', {
    params: { project_id },
  }
  )
  return (response.data);
}

export async function updateMetadata(metadata_id: number | string, value: any): Promise<any> {
  const response = await economxapi.put(`/metadata/${metadata_id}`, {
    value
  })
  return response;
}

export async function registerUser(user: UserSubmit, organization_id: number | string): Promise<any> {
  const response = await economxapi.post('/logins/register', {
    user,
    organization_id,
  });
  return response;
}

export async function inviteToJoin(organization_id: number, user: UsersEntity): Promise<any> {
  const response = await economxapi.post(`/organizations_memberships`, {
    action_type: 'invite',
    organization_id,
    user,
  });
  return response;
}

export async function requestToJoin(organization_id: number): Promise<any> {
  const response = await economxapi.post(`/organizations_memberships`, {
    action_type: 'request',
    organization_id,
  });
  return response;
}

export async function updateOrganizationMembership(action_type: string, user: UsersEntity): Promise<any> {
  const response = await economxapi.put(`/organizations_memberships/${user.membership_id}`, {
    action_type,
    membership: user,
  });
  return response;
}

export async function verifyNewUser(user: IVerify): Promise<any> {
  const response = await economxapi.get(`/users/verify`, {
    params: { user },
  });
  return response;
}

export async function createOrganization(organization: any): Promise<any> {
  const response = await economxapi.post('/business/profile/organizations', {
    organization,
  });
  return response;
}

export async function refreshCurrentUser(): Promise<User> {
  const response = await economxapi.get('/logins/token');
  return (response.data as UserResponse).user;
}

export async function logoutUser(id: number): Promise<any> {
  return await economxapi.delete(`/logins/${id}`).then((result) => {
    clearToken().then((res) => {
      Router.push('/');
      return null;
    });
  });
}

export async function resendVerificationLink(code: string, organization_id: number | string) {
  return await economxapi.post('/users/resend_link', {
    code,
    organization_id
  });
}

export async function resetPasswordLink(email: string) {
  return await economxapi.post('/users/reset_password', {
    username: email,
  });
}

export async function changePassword(payload: IChangePassword) {
  return await economxapi.put('/users/change_password', {
    account: payload,
  });
}

export async function fetchProfile(username: string): Promise<Profile> {
  const response = await economxapi.get(`/profiles/${username}`);
  return (response.data as ProfileResponse).profile;
}

export async function fetchProfileByPath(path: string, organization_id: number): Promise<any> {
  const response = await economxapi.get(`/business/profile/find_by_path`, {
    params: {
      path,
      organization_id,
    },
  });
  return response.data as any;
}

export async function notifyAdmins(id: number): Promise<any> {
  return await economxapi.get(`/business/profile/organizations/${id}/notify_admins`);
}

export async function fetchProfileComplete(id: number): Promise<IProfileComplete> {
  const response = await economxapi.get(`/business/profile/organizations/${id}/profile_complete`);
  return response.data as IProfileComplete;
}

export async function findBusinessByIdentifier(payload: any): Promise<IProfileComplete> {
  const response = await economxapi.get(`/business/profile/find_by_identifier`, {
    params: payload,
  });
  return response.data as IProfileComplete;
}

export async function deactivateProfile(id: number): Promise<any> {
  return await economxapi.delete(`/users/${id}`).then((result) => {
    if (!result) {
      clearToken().then((res) => {
        Router.push('/');
      });
    }
    return result;

  });
}

export async function fetchCountries(type: string): Promise<CountriesEntity[]> {
  const response = await economxapi.get(`/business/countries`, { params: { type } });
  return response.data.countries;
}

export async function fetchTaxonomies(group_name: string, parent: string, which: string): Promise<any> {
  const response = await economxapi.get(`/business/taxonomy/?which=${which}&group_name=${group_name}&parent=${parent}`);
  return response.data.taxonomies;
}

export async function fetchBusinessProfile(orgId: number): Promise<BusinessProfile> {
  const response = await economxapi.get(`/business/profile/organizations/${orgId}`);
  return response.data as BusinessProfileResponse;
}

export async function fetchSupplierProfileByPath(path: string, with_targets: boolean): Promise<Organization> {
  const response = await economxapi.get(`/business/profile/supplier`, {
    params: {
      path,
      with_targets,
    },
  });
  return response.data as Organization;
}

export async function deleteAttachment(payload: any): Promise<any> {
  const response = await economxapi.delete(`/attachments/${payload.id}?service=${payload.service}`, {
    params: { linked_to: payload.linkedTo },
  });
  return response.data;
}

export async function fetchBusinessProfileOrg(orgId: number): Promise<BusinessProfileOrg> {
  const response = await economxapi.get(`/business/organizationorg/${orgId}`);
  return response.data as BusinessProfileOrgResponse;
}

export async function fetchBusinessInsuranceAndCertificates(orgId: number): Promise<BusinessInsuranceAndCertificates> {
  const response = await economxapi.get(`/business/profile/insurance_and_certificates/${orgId}`);
  return response.data as BusinessInsuranceAndCertificates;
}

export async function fetchBusinessProfileUsers(orgId: number): Promise<BusinessProfileUsers> {
  const response = await economxapi.get(`/business/profile/organizationusers/${orgId}`);
  return response.data as BusinessProfileUsersResponse;
}

export async function fetchSuppliersList(projectId: number, filters: any[]): Promise<SuppliersListResponse> {
  const response = await economxapi.get(`/supplier_search/${projectId}`, {
    params: {
      filters,
    },
  });
  return response.data as SuppliersListResponse;
}

export async function fetchMatchingSuppliers(filters: any[]): Promise<PublicSupplierSearchList> {
  const response = await economxapi.get('suppliers', {
    params: {
      filters,
    },
  });
  return response.data as PublicSupplierSearchList;
}

export async function fetchMatchingSuppliersForProject(
  project_id: number | string,
  filters: any[],
): Promise<PublicSupplierSearchList> {
  const response = await economxapi.get('suppliers', {
    params: {
      project_id,
      filters,
    },
  });
  return response.data as PublicSupplierSearchList;
}

export async function fetchNeraMatchingSuppliers(
  project_id: string | string[],
  filters: any[],
): Promise<PublicSupplierSearchList> {
  const response = await economxapi.get(`/${project_id}/suppliers/public`, {
    params: {
      filters,
    },
  });
  return response.data as PublicSupplierSearchList;
}

export async function fetchSingleSupplyChain(id: string | number): Promise<SingleSupplyChainResponse> {
  const response = await economxapi.get(`projects/${id}`, {
    params: {
      suppliers_list: false,
      with_form: true,
      with_stats: false,
    },
  });
  return response.data as SingleSupplyChainResponse;
}

export async function fetchSingleProject(id: string | number): Promise<SingleSupplyChainResponse> {
  const response = await economxapi.get(`view_project/${id}`, {
    params: {
      suppliers_list: false,
      with_form: true,
      with_stats: false,
    },
  });
  return response.data as SingleSupplyChainResponse;
}

export async function fetchProjectSupplyChain(id: string | number): Promise<ProjectSupplyChain> {
  const response = await economxapi.get(`projects/${id}`, {
    params: {
      suppliers_list: false,
      with_form: false,
      with_stats: true,
    },
  });
  return response.data as ProjectSupplyChainResponse;
}

export async function fetchProjectWithForm(id: string): Promise<SingleSupplyChainResponse> {
  const response = await economxapi.get(`projects/${id}`, {
    params: {
      suppliers_list: false,
      with_form: true,
      with_stats: false,
    },
  });
  return response.data as SingleSupplyChainResponse;
}

export async function fetchProjectsList(): Promise<SupplyChains> {
  const response = await economxapi.get(`/projects_list`);
  return response.data as SupplyChainsResponse;
}

export async function fetchMySupplyChains(): Promise<SupplyChains[]> {
  const response = await economxapi.get(`/projects?which=my_projects&with_children=true`);
  return response.data as SupplyChains[];
}

export async function fetchRegisteredSupplyChains(): Promise<SupplyChains> {
  const response = await economxapi.get(`/projects?which=registered_projects`);
  return response.data as SupplyChainsResponse;
}

export async function fetchSupplyChainsByOrg(organization_id: number | string): Promise<SupplyChains[]> {
  const response = await economxapi.get(`/projects?which=by_organization&organization_id=${organization_id}`);
  return response.data as SupplyChains[];
}

export async function fetchAllSupplyChains(): Promise<SupplyChains> {
  const response = await economxapi.get(`/projects?which=view_projects`);
  return response.data as SupplyChainsResponse;
}

export async function fetchCapabilitiesList(): Promise<CapabilitiesList[]> {
  const response = await economxapi.get(`/business/tags`);
  return response.data as CapabilitiesListResponse[];
}

export async function fetchRootCategoriesList(): Promise<BusinessCategory[]> {
  const response = await economxapi.get(`/business/categories?which=root`);
  return response.data as BusinessCategory[];
}

export async function fetchCategoriesList(): Promise<BusinessCategory[]> {
  const response = await economxapi.get(`/business/categories`);
  return response.data as BusinessCategory[];
}

export async function fetchProfileCapabilitiesList(id: number | string): Promise<BusinessCategory[]> {
  const response = await economxapi.get(`/business/categories`, {
    params: { which: 'by_profile', profile_id: id },
  });
  return response.data as BusinessCategory[];
}

export async function fetchProjectCapabilitiesList(id: number | string): Promise<BusinessCategory[]> {
  const response = await economxapi.get(`/business/categories`, {
    params: { project_id: id },
  });
  return response.data as BusinessCategory[];
}

export async function fetchUser(userNum: string): Promise<UsersEntity> {
  const response = await economxapi.get('/users' + '/' + userNum);
  return response.data as UsersEntity;
}

export async function updateUser(user: UsersEntity | MRContactUser): Promise<any> {
  const response = await economxapi.put(`/users/${user.id}`, { user });
  return response.data as UsersEntity | MRContactUser;
}

export async function getUser(userId: number): Promise<any> {
  return await economxapi.get(`/users/${userId}`);
}

export async function businessUsers(params: any): Promise<UsersEntity[]> {
  const response = await economxapi.get('/users', {
    params,
  });
  return response.data as UsersEntity[];
}

// ***********************
// Case Study Routes
// ***********************
export async function fetchCaseStudiesByProfile(profile_id?: number | null): Promise<CaseStudy[]> {
  const response = await economxapi.get('/business/case_studies', {
    params: { profile_id },
  });
  return response.data as CaseStudy[];
}

export async function deleteCaseStudy(id: number): Promise<any> {
  return await economxapi.delete(`/business/case_studies/${id}`);
}

// ***********************
// Product and Services Routes
// ***********************
export async function fetchProductServicesByProfile(profile_id?: number | null): Promise<IProductService[]> {
  const response = await economxapi.get('/business/product_services', {
    params: { profile_id },
  });
  return response.data as IProductService[];
}

export async function getProductService(id: number): Promise<any> {
  return await economxapi.get(`/business/product_services/${id}`);
}

export async function deleteProductService(id: number): Promise<any> {
  return await economxapi.delete(`/business/product_services/${id}`);
}

// ***********************
// Profile Billing Routes
// ***********************
export async function fetchProfileBilling(profile_id: number): Promise<any> {
  return await economxapi.get(`/business/profile/${profile_id}/profile_billing`);
}

export async function updateProfileBilling(id: number, profile_billing: BillingDetails): Promise<any> {
  return await economxapi.put(`/business/profile/billings/${id}`, {
    profile_billing,
  });
}

export async function createProfileBilling(profile_billing: BillingDetails): Promise<any> {
  return await economxapi.post(`/business/profile/billings`, {
    profile_billing,
  });
}

export async function verifyBillingEmail(code: string, billing_email: string): Promise<any> {
  return await economxapi.post(`/business/profile/billings/${code}/verify`, {
    billing_email,
  });
}

export async function setBillingVerificationCode(id: number | string): Promise<any> {
  return await economxapi.get(`/business/profile/billings/${id}/set_verification_code`);
}

// ***********************
// Licence Routes
// ***********************

export async function fetchLicenceDefinitions(): Promise<any> {
  return await economxapi.get(`licence/definitions`);
}

export async function fetchPaymentMethods(business_id: string | number): Promise<any> {
  return await economxapi.get(`licence/customers/${business_id}/payment_methods`);
}

export async function detachPaymentMethod(business_id: string | number, payment_method: string): Promise<any> {
  return await economxapi.delete(`licence/customers/${business_id}/detach_payment_method/${payment_method}`);
}

export async function defaultPaymentMethod(business_id: string | number, payment_method: string): Promise<any> {
  return await economxapi.put(`licence/customers/${business_id}/default_payment_method/${payment_method}`);
}

export async function canDowngrade(business_id: string | number, licence_definition_id: string): Promise<any> {
  return await economxapi.get(`licence/definitions/can_downgrade`, {
    params: {
      business_id,
      licence_definition_id,
    }
  });
}

export async function updateSubscription(id: string, business_id: number, seats: number): Promise<any> {
  return await economxapi.put(`/licence/subscriptions/${id}`, {
    business_id,
    seats,
  })
}

export async function contactUs(details: any): Promise<any> {
  return await economxapi.post(`licence/definitions/contact_us`, {
    details
  });
}

export async function customerInvoices(business_id: number | string): Promise<any> {
  return await economxapi.get(`licence/customers/${business_id}/invoices`)
}
// ***********************
// Subscription Routes
// ***********************

export async function changeRequests(business_id: number): Promise<any> {
  return await economxapi.get(`licence/${business_id}/change_requests`);
}

export async function createSubscription(payload: any): Promise<any> {
  return await economxapi.post(`licence/subscriptions`, payload);
}

export async function currentSubscription(business_id: number | string): Promise<any> {
  return await economxapi.get(`licence/${business_id}/current_subscription`);
}

export async function subscriptionChangeRequest(price_id: string, card: any = null, payment_method: string | null, purchased_seats: number): Promise<any> {
  return await economxapi.post(`/licence/change_requests`,
    {
      price_id,
      card,
      payment_method,
      purchased_seats,
    }
  );
}

// ***********************
// Payment method routees
// ***********************

export async function createPaymentMethod(card: any = null, business_id: number | string): Promise<any> {
  return await economxapi.post(`/licence/customers/${business_id}/create_payment_method`,
    {
      card,
    }
  );
}


// ***********************
// Communication Routes
// ***********************

export async function fetchComms(which: string, context_id?: number, context_type?: string): Promise<CommsEntity[]> {
  const response = await economxapi.get('/comms/', {
    params: {
      which,
      context_id,
      context_type,
    },
  });
  return (response.data as Communications).comms;
}

export async function fetchEdos(columns: string[]): Promise<EdosEntity[]> {
  const response = await economxapi.get(`/edos/`, { params: { columns } });
  return (response.data as Edos).edos;
}

// ***********************
// Project Routes
// ***********************

export async function createProject(project: SupplyChains): Promise<Project> {
  economxapi.defaults.headers = { 'Content-Type': 'multipart/form-data' };
  const response = await economxapi.post(`/projects`, Helpers.ObjectToFormData(project, 'project'));
  return response.data as Project;
}

export async function updateProject(project: SupplyChains): Promise<Project> {
  economxapi.defaults.headers = { 'Content-Type': 'multipart/form-data' };
  const response = await economxapi.put(`/projects/${project.id}`, Helpers.ObjectToFormData(project, 'project'));
  return response.data as Project;
}

export async function getProject(project: ProjectSupplyChainResponse): Promise<SingleSupplyChain> {
  const response = await economxapi.get(`/projects/${project.id}`);
  return response.data as SingleSupplyChain;
}

export async function getProjectCategories(project_id: number | string): Promise<BusinessCategory[]> {
  const response = await economxapi.get(`/project_categories/${project_id}`);
  return response.data as BusinessCategory[];
}

export async function fetchProjectCategoriesByToken(project_id: string | string[]): Promise<BusinessCategory[]> {
  const response = await economxapi.get(`${project_id}/categories`);
  return response.data as BusinessCategory[];
}

export async function setProjectCategories(
  id: number,
  categories: BusinessCategory[],
  destroyed_categories: number[] | null = null,
): Promise<any> {
  const response = await economxapi.post(`/set_project_categories/${id}`, {
    categories,
    destroyed_categories,
  });
  return response.data as BusinessCategory[];
}

export async function deactivateProject(id: number): Promise<any> {
  return await economxapi.delete(`projects/${id}`);
}

// ***********************
// Location Routes
// ***********************

export async function fetchLocations(which: string, ownerId: number): Promise<LocationsAttributesEntity[]> {
  const response = await economxapi.get(`/business/locations`, {
    params: {
      which,
      owner_id: ownerId,
    },
  });
  return response.data.locations;
}

// ***********************
// Supplier Interest
// ***********************

export async function submitInterest(interest: IInterest): Promise<IInterest> {
  const response = await economxapi.post(`/supplier_project_interests`, {
    response: interest,
  });
  return response.data as IInterest;
}

export async function fetchSupplierInterestCategories(
  profile_id: number,
  project_id: number,
): Promise<BusinessCategory[]> {
  const response = await economxapi.get('supplier_interest_categories', {
    params: {
      project_id,
      profile_id,
    },
  });
  return response.data as BusinessCategory[];
}

export async function fetchSupplierCategories(profile_id: number | string): Promise<BusinessCategory[]> {
  const response = await economxapi.get('supplier_categories', {
    params: {
      profile_id,
    },
  });
  return response.data as BusinessCategory[];
}

export async function setSupplierCategories(id: number, project_id: number, category_ids: number[], destroyed_category_ids: number[]): Promise<any> {
  const response = await economxapi.post(`/set_supplier_categories/${id}`, {
    category_ids,
    destroyed_category_ids,
    project_id,
  });
  return response.data as BusinessCategory[];
}

// ***********************
// Market Request Response Routes
// ***********************

export async function fetchMarketRequestComms(id: any): Promise<any> {
  const response = await economxapi.get(`/comms`, {
    params: {
      context_id: id,
      context_type: 'MarketRequest',
      which: 'announcements',
    },
  });
  return response.data.comms as any;
}

export async function fetchMarketRequestDirectComms(id: any, pid: any, pone: any, ptwo: any): Promise<any> {
  const response = await economxapi.get(`/correspondences`, {
    params: {
      which: 'by_participants',
      project_id: pid,
      context: {
        id,
        type: 'MarketRequest',
      },
      participant_one: {
        id: pone.id,
        type: pone.type,
      },
      participant_two: {
        id: ptwo.id,
        type: ptwo.type,
      },
    },
  });
  return response.data.comms as any;
}

export async function fetchMarketRequestByBusiness(id: any): Promise<any> {
  const response = await economxapi.get(`/show_by_business?market_request_id=${id}`);
  console.log('mr response', response);
  return response.data.market_request as any;
}

export async function fetchMarketRequests(which: string, filters: any, output_format: string): Promise<any> {
  const response = await economxapi.get(`/views`, {
    params: {
      which,
      filters,
      output_format,
    },
  });
  return response.data.market_requests as any;
}

export async function exportMarketRequests(which: string, filters: any, output_format: string): Promise<any> {
  const response = await economxapi.get(`/views`, {
    params: {
      which,
      filters,
      output_format,
    },
  });
  return response.data as any;
}

export async function createMr(market_request: IMarketRequest): Promise<any> {
  const response = await economxapi.post(`/market_requests`, { market_request });
  return response.data as any;
}

export async function updateMr(market_request: IMarketRequest): Promise<any> {
  const response = await economxapi.put(`/market_requests/${market_request.id}`, { market_request });
  return response.data as any;
}

export async function deleteMr(market_request_id: number): Promise<any> {
  return await economxapi.delete(`/market_requests/${market_request_id}`);
}

export async function createMrResponse(mrs: any): Promise<any> {
  const response = await economxapi.post(`/market_request_responses`, {
    market_request_response: mrs,
  });
  return response.data as any;
}

export async function updateMrResponse(mrs: any): Promise<any> {
  const response = await economxapi.put(`/market_request_responses/${mrs.id}`, {
    market_request_response: mrs,
  });
  return response.data as any;
}

export async function fetchProjectMarketRequests(searchData: any): Promise<any> {
  const response = await economxapi.get(`/market_requests`, {
    params: { which: searchData.which, project_id: searchData.project_id, status_values: searchData.status_values },
  });
  return response.data.market_requests as any;
}

export async function fetchMarketRequest(searchData: any): Promise<any> {
  const response = await economxapi.get(`/market_requests/${searchData.mr_id}`, {
    params: { without_elements: searchData.without_elements },
  });
  return response.data.market_request as any;
}

export async function fetchMRResponses(searchData: any): Promise<any> {
  const response = await economxapi.get(`/market_request_responses`, {
    params: searchData,
  });
  return response.data as any;
}

export async function fetchMRResponse(searchData: any): Promise<any> {
  const response = await economxapi.get(`/market_request_responses/${searchData.id}`, {
    params: { with_profile: searchData.with_profile },
  });
  return response.data as any;
}

export async function defaultMRForm(type: string): Promise<any> {
  const response = await economxapi.get(`/forms/${type}`);
  return response.data as any;
}

export async function fetchCertificateTypes(): Promise<CertificatesEntity[]> {
  const response = await economxapi.get(`business/certificates`);
  return (response.data as CertificateTypes).certificates;
}

export async function fetchInsuranceTypes(): Promise<any> {
  const response = await economxapi.get(`business/insurances`);
  return response;
}

export async function sendNewCorrespondence(obj: any): Promise<any> {
  console.log("obj", obj);
  const response = await economxapi.post(`correspondences`, obj);
  return response.data;
}

export async function sendNewAnnouncement(obj: any): Promise<any> {
  const response = await economxapi.post(`comms`, obj);
  return response.data;
}

export async function updateTimer(timerData: ITimerUpdate): Promise<any> {
  const response = await economxapi.post('update_timer', timerData);
  return response;
}

export async function fetchLicences(fetchData: any): Promise<any> {
  const response = await economxapi.get(`licence/licences`, { params: { which: fetchData.which, id: fetchData.id } });
  return response.data as any;
}

export async function fetchCorrespondences(params: ICorrespondenceGetParams): Promise<any> {
  const response = await economxapi.get(`correspondences`, { params: { params } });
  return response.data.correspondences as any;
}

export async function fetchQuestionCategories(): Promise<any> {
  const response = await economxapi.get(`question_categories`);
  return response.data.question_categories as any;
}

export async function fetchQuestions(params: any): Promise<any> {
  const response = await economxapi.get(`questions`, { params });
  return response.data.questions as any;
}

// ***********************
// Revisions
// ***********************

export async function fetchRevisions(searchData: any): Promise<any> {
  const response = await economxapi.get(`/revisions`, { params: searchData });
  return response.data.response as any;
}

export async function fetchMRFeedback(payload: any): Promise<any> {
  const response = await economxapi.get(`/feedbacks`, { params: payload });
  return response.data;
}

export async function updateMRFeedback(payload: any): Promise<any> {
  const response = await economxapi.put(`/feedbacks/${payload.id}`, payload);
  return response.data;
}

export async function fetchRevision(searchData: any): Promise<any> {
  const response = await economxapi.get(`/revisions/${searchData.id}`, {
    params: { project_id: searchData.project_id, responses: searchData.responses },
  });
  return response.data.revision as any;
}

export async function createRevision(searchData: any): Promise<any> {
  const response = await economxapi.post(`/revisions/`, { revision: searchData });
  return response.data.revision as any;
}

export async function updateRevision(searchData: any): Promise<any> {
  const response = await economxapi.put(`/revisions/${searchData.id}`, {
    project_id: searchData.project_id,
    type: searchData.type,
  });
  return response.data.revision as any;
}

export async function createRevisionResponse(payload: any): Promise<any> {
  const response = await economxapi.post('/revision_responses', {
    revision_response: payload.revision_response,
    project_id: payload.project_id,
  });

  return response.data as any;
}

export async function unshortlistRevisionResponse(payload: any): Promise<any> {
  return await economxapi.delete(
    `/revision_responses/${payload.id}?project_id=${payload.project_id}&market_request_id=${payload.market_request_id}`,
  );
}

// ***********************
// Feedback
// ***********************

export async function fetchUserSurvey(type: string): Promise<any> {
  const response = await economxapi.get(`/ecx_surveys/surveys/${type}`, { params: { by_type: true } });
  return response.data;
}

export async function fetchSurveys(profile_id: number | string): Promise<any> {
  const response = await economxapi.get(`/ecx_surveys/surveys`, { params: { profile_id } });
  return response.data;
}

export async function fetchSurvey(id: number | string): Promise<any> {
  const response = await economxapi.get(`/ecx_surveys/surveys/${id}`);
  return response.data;
}

export async function updateSurveyPath(survey: any): Promise<any> {
  const response = await economxapi.put(`/ecx_surveys/surveys/${survey.id}?which=external_link`, {
    survey
  });
  return response.data;
}

export async function closeSurvey(survey_id: any): Promise<any> {
  const response = await economxapi.put(`/ecx_surveys/surveys/${survey_id}?which=close`);
  return response.data;
}

export async function publishSurvey(survey_id: any): Promise<any> {
  const response = await economxapi.put(`/ecx_surveys/surveys/${survey_id}?which=publish`);
  return response.data;
}

export async function deleteSurvey(survey_id: any): Promise<any> {
  const response = await economxapi.delete(`/ecx_surveys/surveys/${survey_id}`);
  return response.data;
}

export async function findSurveyByPath(path: string, check_existing: boolean): Promise<any> {
  const response = await economxapi.get(`/ecx_surveys/survey_by_path`, {
    params: {
      path,
      check_existing
    }
  });
  return response.data;
}

export async function fetchSurveyResponses(surveyId: number | string): Promise<any> {
  return await economxapi.get(`/ecx_surveys/survey_responses/`, {
    params: {
      survey_id: surveyId
    }
  });
}

export async function fetchSurveyResponse(survey_response_id: number | string): Promise<any> {
  return await economxapi.get(`/ecx_surveys/survey_responses/${survey_response_id}`);
}

export async function UpdateUserSurveyResponse(payload: any): Promise<any> {
  console.log('payload', payload);
  return await economxapi.put(`/ecx_surveys/survey_responses/${payload.id}`, { response: payload.response });
}

export async function submitMRFeedback(payload: MRFBPayload): Promise<any> {
  const response = await economxapi.post(`/feedbacks`, payload);
  return response.data;
}

export async function submitGeneralFeedbackData(generalFeedback: GeneralFeedback): Promise<any> {
  console.log('generalFeedback', generalFeedback);
  const response = await economxapi.post('/feedbacks', generalFeedback);
  return response;
}

export async function submitUserFeedbackData(id: number, userFeedback: UserFeedbackResponse): Promise<any> {
  console.log('userFeedback', userFeedback);
  const response = await economxapi.put(`/ecx_surveys/survey_responses/${id}`, userFeedback);
  return response;
}

// ***********************
// Feedback Template
// ***********************

export async function fetchFeedbackTemplate(searchData: any): Promise<any> {
  const response = await economxapi.get(`/feedback_templates/${searchData.id}`, {
    params: { by_type: searchData.by_type },
  });
  return response.data as any;
}
